import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';

// Press and hold someone's picture to see it large; let go and it's gone. Works with a finger or
// a mouse. Moving before it opens means you're scrolling, so it doesn't open. While it's open the
// press that opened it doesn't also "click" whatever the picture sits in (a menu, Settings' upload).

const HOLD_MS = 350;
const SLOP_PX = 10;

// A manual popover sits in the top layer, so the enlarged picture also shows above open dialogs.
const showOnTop = (el: HTMLDivElement | null) => {
  try {
    el?.showPopover();
  } catch {
    /* no popover support: still shows, fixed over the page */
  }
};

/** The enlarged size of a picture URL from avatarUrl (served at up to 768 px). */
const largeOf = (src: string) => `${src}${src.includes('?') ? '&' : '?'}size=large`;

export function useAvatarPeek(src: string | undefined, name: string) {
  const [open, setOpen] = useState(false);
  const timer = useRef(0);
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  const opened = useRef(false);

  const cancel = () => {
    clearTimeout(timer.current);
    start.current = null;
  };
  const close = () => {
    cancel();
    setOpen(false);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const handlers = src
    ? {
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
          if (e.button !== 0) return;
          start.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
          opened.current = false;
          new Image().src = largeOf(src); // start loading the big one now
          const el = e.currentTarget;
          timer.current = window.setTimeout(() => {
            opened.current = true;
            try {
              el.setPointerCapture(e.pointerId); // keep getting the release even if the finger drifts
            } catch {
              /* pointer already gone */
            }
            navigator.vibrate?.(8);
            setOpen(true);
          }, HOLD_MS);
        },
        onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
          const s = start.current;
          if (s && !opened.current && Math.hypot(e.clientX - s.x, e.clientY - s.y) > SLOP_PX) cancel();
        },
        onPointerUp: close,
        onPointerCancel: close,
        onLostPointerCapture: close,
        // The release after a hold isn't a tap on whatever holds the picture.
        onClickCapture: (e: ReactMouseEvent) => {
          if (opened.current) {
            e.preventDefault();
            e.stopPropagation();
            opened.current = false;
          }
        },
        // No "save image" / text-selection menu on a long press.
        onContextMenu: (e: ReactMouseEvent) => e.preventDefault(),
      }
    : {};

  const overlay =
    open && src
      ? createPortal(
          <div className="avatar-peek" aria-hidden popover="manual" ref={showOnTop}>
            <figure>
              <img src={largeOf(src)} alt="" draggable={false} />
              <figcaption>{name}</figcaption>
            </figure>
          </div>,
          document.body,
        )
      : null;

  return { handlers, overlay };
}
